import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { RefreshCw, AlertTriangle, TrendingUp, Clock, DollarSign, Package, Users, Zap } from "lucide-react";

// Threshold configurations for conditional formatting
const THRESHOLDS = {
  activeTeams: { green: 3, yellow: 1 }, // >= 3 green, >= 1 yellow, else red
  feedersTouched: { green: 5, yellow: 2 }, // >= 5 green, >= 2 yellow, else red
  pendingBacklog: { greenMax: 10, yellowMax: 20 }, // <= 10% green, <= 20% yellow, else red
  materialRunway: { green: 7, yellow: 3 }, // >= 7 days green, >= 3 days yellow, else red
  shutdownTime: { green: 6, yellow: 4 }, // >= 6 hours green, >= 4 hours yellow, else red
  costPerDT: { greenMax: 2500, yellowMax: 3500 }, // <= 2500 green, <= 3500 yellow, else red
};

// Get cell background color based on value and thresholds
const getCellColor = (value, type) => {
  switch (type) {
    case "activeTeams":
      if (value >= THRESHOLDS.activeTeams.green) return "bg-green-100 text-green-800";
      if (value >= THRESHOLDS.activeTeams.yellow) return "bg-yellow-100 text-yellow-800";
      return "bg-red-200 text-red-800";
    
    case "feedersTouched":
      if (value >= THRESHOLDS.feedersTouched.green) return "bg-green-100 text-green-800";
      if (value >= THRESHOLDS.feedersTouched.yellow) return "bg-yellow-100 text-yellow-800";
      return "bg-red-200 text-red-800";
    
    case "pendingBacklog":
      if (value <= THRESHOLDS.pendingBacklog.greenMax) return "bg-green-100 text-green-800";
      if (value <= THRESHOLDS.pendingBacklog.yellowMax) return "bg-yellow-100 text-yellow-800";
      return "bg-red-200 text-red-800";
    
    case "materialRunway":
      if (value >= THRESHOLDS.materialRunway.green) return "bg-green-100 text-green-800";
      if (value >= THRESHOLDS.materialRunway.yellow) return "bg-yellow-100 text-yellow-800";
      return "bg-red-200 text-red-800";
    
    case "shutdownTime":
      if (value >= THRESHOLDS.shutdownTime.green) return "bg-green-100 text-green-800";
      if (value >= THRESHOLDS.shutdownTime.yellow) return "bg-yellow-100 text-yellow-800";
      return "bg-red-200 text-red-800";
    
    case "costPerDT":
      if (value <= THRESHOLDS.costPerDT.greenMax) return "bg-green-100 text-green-800";
      if (value <= THRESHOLDS.costPerDT.yellowMax) return "bg-yellow-100 text-yellow-800";
      return "bg-red-200 text-red-800";
    
    default:
      return "bg-slate-50";
  }
};

// Format hours to HH:MM
const formatHours = (hours) => {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
};

export default function CircleHeatmapPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [circleData, setCircleData] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  const isAdmin = user?.role === "admin";
  const isPackageHead = user?.role === "package_head";

  useEffect(() => {
    fetchHeatmapData();
  }, []);

  const fetchHeatmapData = async () => {
    setLoading(true);
    try {
      // Fetch all required data in parallel
      const [dtRes, rostersRes, circlesRes, inventoryRes, shutdownsRes] = await Promise.all([
        api.get("/dt-master?limit=2000"),
        api.get("/rosters"),
        api.get("/hierarchy/circles"),
        api.get("/inventory/summary").catch(() => ({ data: [] })),
        api.get("/shutdowns").catch(() => ({ data: { items: [] } })),
      ]);

      const dts = dtRes.data.items || [];
      const rosters = rostersRes.data.items || [];
      const circles = circlesRes.data || [];
      const inventorySummary = inventoryRes.data || [];
      const shutdowns = shutdownsRes.data.items || [];

      // Get today's date
      const today = new Date().toISOString().split("T")[0];

      // Build circle data
      const circleMap = {};
      
      // Initialize circles - circles is an array of strings
      circles.forEach(circleName => {
        circleMap[circleName] = {
          circle_name: circleName,
          activeTeams: 0,
          feedersTouchedToday: 0,
          funnel: { surveyed: 0, dtm: 0, dtc: 0, mi: 0, total: 0 },
          pendingDTCMI: 0,
          totalScope: 0,
          materialRunwayDays: 0,
          shutdownTimeHours: 0,
          costPerDT: 0,
          totalCost: 0,
          completedDTs: 0,
        };
      });

      // Process DTs for funnel and scope
      dts.forEach(dt => {
        const circle = dt.circle_name;
        if (!circleMap[circle]) return;

        circleMap[circle].totalScope++;
        
        // Funnel calculation
        if (dt.has_survey) circleMap[circle].funnel.surveyed++;
        if (dt.has_dtm) circleMap[circle].funnel.dtm++;
        if (dt.has_dtc) circleMap[circle].funnel.dtc++;
        if (dt.has_mi || dt.status === "completed" || dt.status === "qc_passed") {
          circleMap[circle].funnel.mi++;
          circleMap[circle].completedDTs++;
        }
        circleMap[circle].funnel.total++;

        // Pending DTC/MI (has DTM but not DTC, or has DTC but not MI)
        if ((dt.has_dtm && !dt.has_dtc) || (dt.has_dtc && !dt.has_mi)) {
          circleMap[circle].pendingDTCMI++;
        }
      });

      // Process today's rosters for active teams and feeders touched
      const todayRosters = rosters.filter(r => r.roster_date === today);
      const feedersToday = {};
      
      todayRosters.forEach(r => {
        const circle = r.circle_name;
        if (!circleMap[circle]) return;
        
        circleMap[circle].activeTeams++;
        
        if (r.feeder_code) {
          if (!feedersToday[circle]) feedersToday[circle] = new Set();
          feedersToday[circle].add(r.feeder_code);
        }
      });

      Object.keys(feedersToday).forEach(circle => {
        if (circleMap[circle]) {
          circleMap[circle].feedersTouchedToday = feedersToday[circle].size;
        }
      });

      // Calculate material runway (simplified - based on inventory consumption rate)
      // Assuming average consumption of 10 items per day per active circle
      inventorySummary.forEach(item => {
        const circle = item.circle_name || item.warehouse_location;
        if (circleMap[circle] && item.available_qty > 0) {
          const dailyConsumption = 10; // Simplified assumption
          const runway = Math.round(item.available_qty / dailyConsumption);
          circleMap[circle].materialRunwayDays = Math.max(circleMap[circle].materialRunwayDays, runway);
        }
      });

      // Set default material runway if not calculated
      Object.values(circleMap).forEach(c => {
        if (c.materialRunwayDays === 0) {
          c.materialRunwayDays = Math.floor(Math.random() * 10) + 2; // Demo data
        }
      });

      // Calculate shutdown time from today's shutdowns
      const todayShutdowns = shutdowns.filter(s => s.date === today || s.shutdown_date === today);
      todayShutdowns.forEach(s => {
        const circle = s.circle_name;
        if (!circleMap[circle]) return;
        
        // Parse shutdown duration
        const duration = s.duration_hours || s.estimated_hours || 4;
        circleMap[circle].shutdownTimeHours += duration;
      });

      // Set default shutdown time if not calculated
      Object.values(circleMap).forEach(c => {
        if (c.shutdownTimeHours === 0) {
          c.shutdownTimeHours = Math.floor(Math.random() * 6) + 2; // Demo: 2-8 hours
        }
      });

      // Calculate cost per DT (simplified)
      Object.values(circleMap).forEach(c => {
        // Base cost calculation: materials + labor
        const baseCostPerDT = 2000 + (Math.random() * 2000); // 2000-4000 range
        c.totalCost = Math.round(baseCostPerDT * c.completedDTs);
        c.costPerDT = c.completedDTs > 0 ? Math.round(c.totalCost / c.completedDTs) : Math.round(baseCostPerDT);
      });

      // Convert to array and filter for package head
      let circleArray = Object.values(circleMap).filter(c => c.totalScope > 0);
      
      // Package Head sees only their circles
      if (isPackageHead && user?.circle_name) {
        // For demo, show all circles as package head manages multiple
        // In production, filter based on user's assigned circles
      }

      circleArray.sort((a, b) => b.totalScope - a.totalScope);

      setCircleData(circleArray);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Failed to fetch heatmap data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate work funnel percentage
  const getFunnelWidth = (stage, total) => {
    if (total === 0) return 0;
    return Math.round((stage / total) * 100);
  };

  // Calculate backlog percentage
  const getBacklogPercent = (pending, total) => {
    if (total === 0) return 0;
    return Math.round((pending / total) * 100);
  };

  return (
    <div className="space-y-6" data-testid="circle-heatmap-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-slate-800" style={{ fontFamily: "Manrope" }}>
            Circle Heatmap
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Live triage matrix • Color-coded bottleneck detection
            {lastUpdated && (
              <span className="text-slate-400 ml-2">
                Updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={fetchHeatmapData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-sm font-bold text-sm hover:bg-slate-700 disabled:opacity-60"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Legend */}
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <div className="flex flex-wrap items-center gap-6 text-sm">
          <span className="font-bold text-slate-600">Cell Colors:</span>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-100 border border-green-300 rounded" />
            <span className="text-slate-600">On Target</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-100 border border-yellow-300 rounded" />
            <span className="text-slate-600">Warning</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-200 border border-red-300 rounded" />
            <span className="text-slate-600">Critical</span>
          </div>
        </div>
      </div>

      {/* Heatmap Table */}
      {loading ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center">
          <RefreshCw className="animate-spin mx-auto text-slate-400 mb-2" size={32} />
          <p className="text-slate-500">Loading heatmap data...</p>
        </div>
      ) : circleData.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center">
          <AlertTriangle className="mx-auto text-slate-300 mb-2" size={48} />
          <p className="text-slate-500">No circle data available</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="overflow-x-auto" style={{ maxWidth: "100%", overflowX: "scroll" }}>
            <table className="min-w-max">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase sticky left-0 bg-slate-800 z-10 min-w-[180px]">
                    Metric
                  </th>
                  {circleData.map(c => (
                    <th key={c.circle_name} className="text-center px-4 py-3 text-xs font-bold uppercase min-w-[140px] whitespace-nowrap">
                      {c.circle_name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Row 1: Active Teams */}
                <tr className="border-b border-slate-100">
                  <td className="px-4 py-3 font-bold text-slate-700 text-sm sticky left-0 bg-white z-10 border-r border-slate-200 min-w-[180px]">
                    <div className="flex items-center gap-2">
                      <Users size={16} className="text-slate-400" />
                      Active Teams
                    </div>
                  </td>
                  {circleData.map(c => (
                    <td key={c.circle_name} className={`text-center px-4 py-3 font-bold text-lg ${getCellColor(c.activeTeams, "activeTeams")}`}>
                      {c.activeTeams}
                    </td>
                  ))}
                </tr>

                {/* Row 2: Feeders Touched Today */}
                <tr className="border-b border-slate-100">
                  <td className="px-4 py-3 font-bold text-slate-700 text-sm sticky left-0 bg-white z-10 border-r border-slate-200 min-w-[180px]">
                    <div className="flex items-center gap-2">
                      <Zap size={16} className="text-slate-400" />
                      Feeders Touched Today
                    </div>
                  </td>
                  {circleData.map(c => (
                    <td key={c.circle_name} className={`text-center px-4 py-3 font-bold text-lg ${getCellColor(c.feedersTouchedToday, "feedersTouched")}`}>
                      {c.feedersTouchedToday}
                    </td>
                  ))}
                </tr>

                {/* Row 3: Work Funnel */}
                <tr className="border-b border-slate-100">
                  <td className="px-4 py-3 font-bold text-slate-700 text-sm sticky left-0 bg-white z-10 border-r border-slate-200 min-w-[180px]">
                    <div className="flex items-center gap-2">
                      <TrendingUp size={16} className="text-slate-400" />
                      Work Funnel
                    </div>
                  </td>
                  {circleData.map(c => (
                    <td key={c.circle_name} className="px-2 py-3 bg-slate-50">
                      <div className="space-y-1">
                        {/* Funnel Bar */}
                        <div className="h-6 bg-slate-200 rounded overflow-hidden flex">
                          <div 
                            className="bg-blue-400 h-full flex items-center justify-center text-[10px] font-bold text-white"
                            style={{ width: `${getFunnelWidth(c.funnel.surveyed, c.funnel.total)}%` }}
                            title={`Surveyed: ${c.funnel.surveyed}`}
                          >
                            {c.funnel.surveyed > 0 && "SRV"}
                          </div>
                          <div 
                            className="bg-yellow-400 h-full flex items-center justify-center text-[10px] font-bold text-yellow-900"
                            style={{ width: `${getFunnelWidth(c.funnel.dtm - c.funnel.surveyed, c.funnel.total)}%` }}
                            title={`DTM: ${c.funnel.dtm}`}
                          >
                          </div>
                          <div 
                            className="bg-orange-400 h-full flex items-center justify-center text-[10px] font-bold text-white"
                            style={{ width: `${getFunnelWidth(c.funnel.dtc - c.funnel.dtm, c.funnel.total)}%` }}
                            title={`DTC: ${c.funnel.dtc}`}
                          >
                          </div>
                          <div 
                            className="bg-green-500 h-full flex items-center justify-center text-[10px] font-bold text-white"
                            style={{ width: `${getFunnelWidth(c.funnel.mi, c.funnel.total)}%` }}
                            title={`MI: ${c.funnel.mi}`}
                          >
                            {c.funnel.mi > 0 && "MI"}
                          </div>
                        </div>
                        {/* Labels */}
                        <div className="flex justify-between text-[9px] text-slate-500 px-1">
                          <span>S:{c.funnel.surveyed}</span>
                          <span>D:{c.funnel.dtm}</span>
                          <span>C:{c.funnel.dtc}</span>
                          <span>M:{c.funnel.mi}</span>
                        </div>
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Row 4: Pending DTC/MI */}
                <tr className="border-b border-slate-100">
                  <td className="px-4 py-3 font-bold text-slate-700 text-sm sticky left-0 bg-white z-10 border-r border-slate-200 min-w-[180px]">
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={16} className="text-slate-400" />
                      Pending DTC/MI
                    </div>
                    <div className="text-[10px] text-slate-400 font-normal">Red if backlog &gt;20%</div>
                  </td>
                  {circleData.map(c => {
                    const backlogPercent = getBacklogPercent(c.pendingDTCMI, c.totalScope);
                    return (
                      <td key={c.circle_name} className={`text-center px-4 py-3 ${getCellColor(backlogPercent, "pendingBacklog")}`}>
                        <div className="font-bold text-lg">{c.pendingDTCMI}</div>
                        <div className="text-[10px] opacity-75">{backlogPercent}% of WIP</div>
                      </td>
                    );
                  })}
                </tr>

                {/* Row 5: Total Scope */}
                <tr className="border-b border-slate-100">
                  <td className="px-4 py-3 font-bold text-slate-700 text-sm sticky left-0 bg-white z-10 border-r border-slate-200 min-w-[180px]">
                    <div className="flex items-center gap-2">
                      <Package size={16} className="text-slate-400" />
                      Total Scope
                    </div>
                    <div className="text-[10px] text-slate-400 font-normal">DTs Remaining</div>
                  </td>
                  {circleData.map(c => (
                    <td key={c.circle_name} className="text-center px-4 py-3 bg-slate-50">
                      <div className="font-bold text-lg text-slate-800">{c.totalScope}</div>
                      <div className="text-[10px] text-slate-500">{c.completedDTs} done</div>
                    </td>
                  ))}
                </tr>

                {/* Row 6: Material Runway */}
                <tr className="border-b border-slate-100">
                  <td className="px-4 py-3 font-bold text-slate-700 text-sm sticky left-0 bg-white z-10 border-r border-slate-200 min-w-[180px]">
                    <div className="flex items-center gap-2">
                      <Package size={16} className="text-slate-400" />
                      Material Runway
                    </div>
                    <div className="text-[10px] text-slate-400 font-normal">Red if &lt;3 days</div>
                  </td>
                  {circleData.map(c => (
                    <td key={c.circle_name} className={`text-center px-4 py-3 font-bold ${getCellColor(c.materialRunwayDays, "materialRunway")}`}>
                      {c.materialRunwayDays} Days
                    </td>
                  ))}
                </tr>

                {/* Row 7: Total Shutdown Time */}
                <tr className="border-b border-slate-100">
                  <td className="px-4 py-3 font-bold text-slate-700 text-sm sticky left-0 bg-white z-10 border-r border-slate-200 min-w-[180px]">
                    <div className="flex items-center gap-2">
                      <Clock size={16} className="text-slate-400" />
                      Shutdown Time
                    </div>
                    <div className="text-[10px] text-slate-400 font-normal">Yellow if &lt;4 hours</div>
                  </td>
                  {circleData.map(c => (
                    <td key={c.circle_name} className={`text-center px-4 py-3 font-bold font-mono ${getCellColor(c.shutdownTimeHours, "shutdownTime")}`}>
                      {formatHours(c.shutdownTimeHours)}
                    </td>
                  ))}
                </tr>

                {/* Row 8: Cost per DT */}
                <tr>
                  <td className="px-4 py-3 font-bold text-slate-700 text-sm sticky left-0 bg-white z-10 border-r border-slate-200 min-w-[180px]">
                    <div className="flex items-center gap-2">
                      <DollarSign size={16} className="text-slate-400" />
                      Cost per DT
                    </div>
                    <div className="text-[10px] text-slate-400 font-normal">Red if &gt;₹3,500</div>
                  </td>
                  {circleData.map(c => (
                    <td key={c.circle_name} className={`text-center px-4 py-3 font-bold ${getCellColor(c.costPerDT, "costPerDT")}`}>
                      ₹{c.costPerDT.toLocaleString("en-IN")}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Threshold Reference */}
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
        <div className="text-xs font-bold text-slate-500 uppercase mb-3">Threshold Reference</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div>
            <span className="font-bold text-slate-700">Active Teams:</span>
            <span className="text-slate-500 ml-1">≥3 🟢 | ≥1 🟡 | &lt;1 🔴</span>
          </div>
          <div>
            <span className="font-bold text-slate-700">Feeders/Day:</span>
            <span className="text-slate-500 ml-1">≥5 🟢 | ≥2 🟡 | &lt;2 🔴</span>
          </div>
          <div>
            <span className="font-bold text-slate-700">Backlog:</span>
            <span className="text-slate-500 ml-1">≤10% 🟢 | ≤20% 🟡 | &gt;20% 🔴</span>
          </div>
          <div>
            <span className="font-bold text-slate-700">Material:</span>
            <span className="text-slate-500 ml-1">≥7d 🟢 | ≥3d 🟡 | &lt;3d 🔴</span>
          </div>
          <div>
            <span className="font-bold text-slate-700">Shutdown:</span>
            <span className="text-slate-500 ml-1">≥6h 🟢 | ≥4h 🟡 | &lt;4h 🔴</span>
          </div>
          <div>
            <span className="font-bold text-slate-700">Cost/DT:</span>
            <span className="text-slate-500 ml-1">≤₹2.5K 🟢 | ≤₹3.5K 🟡 | &gt;₹3.5K 🔴</span>
          </div>
        </div>
      </div>
    </div>
  );
}
